import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Form, 
  Button, 
  Alert, 
  Modal,
  ListGroup,
  Badge,
  Dropdown
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faTrash, 
  faSave, 
  faEdit,
  faEye,
  faComments,
  faReply,
  faArrowRight,
  faDownload,
  faUpload,
  faFileExport,
  faFileImport,
  faCopy
} from '@fortawesome/free-solid-svg-icons';
import smsTemplateService from '../services/smsTemplateService';
import smsApiService from '../services/smsApiService';
import bookingOptionsService from '../services/bookingOptionsService';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';

/**
 * Placeholders the system knows how to fill in.
 *
 * There is no fixed vocabulary in the engine - {{anything}} is replaced from
 * the conversation's context data, so a placeholder works if, and only if,
 * something supplies a matching value. This is therefore a list of what the
 * system can provide, not the only tokens that will parse.
 */
/**
 * Schemes a template can be sent under. The phone number is what the system
 * gives a customer in the generated "we did not understand" reply, so it has
 * to match the scheme - an MTVH customer must not be given the Essex number.
 *
 * Mirrors SCHEME_OPTIONS in KarmaTextBooking.jsx and SMS_SCHEMES in the API's
 * controller/comms.js. Adding a scheme means adding it in all three.
 */
const SCHEME_CHOICES = [
  { id: 'MTVH',  label: 'MTVH - Metropolitan Thames Valley Housing', phone: '0115 784 3060' },
  { id: 'ESSEX', label: 'Essex CC - Warm Homes Local Grant',         phone: '01206 326808' }
];

const schemePhone = (id) =>
  SCHEME_CHOICES.find((scheme) => scheme.id === id)?.phone || '';

const schemeDetail = (id) => SCHEME_CHOICES.find((scheme) => scheme.id === id) || null;

/**
 * Placeholders whose value follows from the chosen scheme. Filled in
 * automatically and shown read-only - typing them by hand is how an MTVH
 * customer ends up with the Essex number.
 */
const SCHEME_DERIVED_TOKENS = {
  SCHEME_NAME: 'schemeName', schemeName: 'schemeName',
  SCHEME_NUMBER: 'phone',    schemeNumber: 'phone',
  COMPANY: 'company',        company: 'company'
};

/** Placeholders normally different for each recipient. */
const PER_RECIPIENT_TOKENS = [
  'firstName', 'lastName', 'title', 'customerName', 'fullName',
  'streetH', 'postcode', 'contactId', 'number'
];

/** Placeholders offered as a dropdown rather than free text. */
const ASSESSOR_TOKENS = ['ASSESSOR', 'assessor'];

/** Every {{token}} used anywhere in the template. */
const collectTemplateTokens = (template) => {
  const found = new Set();
  const scan = (text) => {
    String(text || '').replace(/{{\s*([\w.]+)\s*}}/g, (match, token) => {
      found.add(token);
      return match;
    });
  };

  (template?.stages || []).forEach((stage) => {
    scan(stage.question);
    (stage.answers || []).forEach((answer) => scan(answer.response));
  });
  scan(template?.invalidResponseMessage);

  return Array.from(found).sort((a, b) => a.localeCompare(b));
};

const KNOWN_PLACEHOLDERS = [
  {
    group: 'Per recipient - from the Karma contact',
    hint: 'Filled in for each person when the conversation starts.',
    items: [
      { token: 'customerName', note: 'Full name on the Karma record' },
      { token: 'firstName',    note: 'First name' },
      { token: 'lastName',     note: 'Surname' },
      { token: 'title',        note: 'Mr, Mrs, Ms and so on' },
      { token: 'streetH',      note: 'Street from the contact address' },
      { token: 'postcode',     note: 'Postcode from the contact address' }
    ]
  },
  {
    group: 'From the scheme',
    hint: 'Filled in automatically from the scheme chosen above.',
    items: [
      { token: 'SCHEME_NAME',   note: 'Scheme name, as customers should see it' },
      { token: 'SCHEME_NUMBER', note: 'Phone number for that scheme' },
      { token: 'COMPANY',       note: 'Company delivering the scheme' }
    ]
  },
  {
    group: 'Set on the template',
    hint: 'Use one of these and a box appears below to fill it in.',
    items: [
      { token: 'ASSESSOR',     note: 'Chosen from the shared assessor list' },
      { token: 'OFFICE_HOURS', note: 'For example 9am-5pm Mon-Fri' }
    ]
  }
];

const SMSTemplateBuilder = () => {
  const { instance } = useMsal();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [currentTemplate, setCurrentTemplate] = useState({
    id: null,
    name: '',
    description: '',
    scheme: '',
    invalidResponseMessage: '',
    placeholderValues: {},
    stages: []
  });
  const [assessorOptions, setAssessorOptions] = useState([]);
  // Which text box the user last typed in, so "insert" knows where to put a
  // placeholder. Held in a ref because it must not cause a re-render.
  const lastFocusedFieldRef = useRef(null);
  const [showModal, setShowModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importJson, setImportJson] = useState('');
  const [editingStage, setEditingStage] = useState(null);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'success' });
  const [useBackendStorage, setUseBackendStorage] = useState(true);
  const [showHelp, setShowHelp] = useState(false);
  const [templateCategoryFilter, setTemplateCategoryFilter] = useState('all');

  const isBookingTemplate = useCallback((template) => {
    const name = String(template?.Name || template?.name || '').trim();
    const description = String(template?.Description || template?.description || '').trim();
    return name.startsWith('Karma Text Booking Flow') || description.startsWith('Managed placeholder template for ');
  }, []);

  const filteredTemplates = useMemo(() => {
    if (templateCategoryFilter === 'booking') {
      return templates.filter((template) => isBookingTemplate(template));
    }
    if (templateCategoryFilter === 'general') {
      return templates.filter((template) => !isBookingTemplate(template));
    }
    return templates;
  }, [templates, templateCategoryFilter, isBookingTemplate]);

  // Load templates on component mount
  useEffect(() => {
    loadTemplates();
    // Create a sample template if none exist
    createSampleTemplateIfNeeded();
  }, []);

  // Load templates from service
  const loadTemplates = async () => {
    try {
      if (useBackendStorage) {
        const response = await smsApiService.getTemplates(instance);
        // Ensure all templates have proper timestamps
        const templatesWithTimestamps = (response.data || []).map(template => ({
          ...template,
          createdAt: template.createdAt || new Date().toISOString(),
          updatedAt: template.updatedAt || template.createdAt || new Date().toISOString()
        }));
        setTemplates(templatesWithTimestamps);
      } else {
        const loadedTemplates = smsTemplateService.getAllTemplates();
        setTemplates(loadedTemplates);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      // Fallback to local storage
      const loadedTemplates = smsTemplateService.getAllTemplates();
      setTemplates(loadedTemplates);
      setUseBackendStorage(false);
      showAlert('Backend unavailable, using local storage', 'warning');
    }
  };

  // Create a sample template for demonstration
  const createSampleTemplateIfNeeded = () => {
    const existingTemplates = smsTemplateService.getAllTemplates();
    if (existingTemplates.length === 0) {
      const sampleTemplate = {
        id: Date.now(),
        name: "Energy Survey Template",
        description: "Sample template for energy efficiency survey",
        stages: [
          {
            id: 1,
            question: "Hi! We're conducting an energy efficiency survey. Would you like to participate? It only takes 2 minutes.",
            answers: [
              {
                id: 1,
                text: "YES",
                response: "", // Empty response to prevent double messaging
                nextStageId: 2
              },
              {
                id: 2,
                text: "NO",
                response: "Thanks for your time! Have a great day.", // Only send message when ending
                nextStageId: null
              }
            ],
            isInitial: true,
            defaultResponse: {
              message: "I didn't understand. Please reply with YES or NO - would you like to participate in our energy survey?",
              retryMessage: "Please reply YES or NO to continue.",
              maxRetries: 3,
              nextStageId: null
            }
          },
          {
            id: 2,
            question: "What type of property do you live in?",
            answers: [
              {
                id: 3,
                text: "HOUSE",
                response: "", // Empty - go directly to next question
                nextStageId: 3
              },
              {
                id: 4,
                text: "APARTMENT", 
                response: "", // Empty - go directly to next question
                nextStageId: 4
              }
            ],
            isInitial: false,
            defaultResponse: {
              message: "I didn't understand. Please reply HOUSE or APARTMENT to tell me your property type.",
              retryMessage: "Please reply HOUSE or APARTMENT.",
              maxRetries: 3,
              nextStageId: null
            }
          },
          {
            id: 3,
            question: "Are you interested in solar panels?",
            answers: [
              {
                id: 5,
                text: "YES",
                response: "Excellent! We'll email you solar panel information and financing options. Thanks!",
                nextStageId: null
              },
              {
                id: 6,
                text: "NO", 
                response: "No problem! We'll send you other energy-saving tips instead. Thanks for your time!",
                nextStageId: null
              }
            ],
            isInitial: false,
            defaultResponse: {
              message: "Please respond with 'Yes' or 'No' regarding solar panel interest.",
              retryMessage: "I need a 'Yes' or 'No' answer about solar panels to continue.",
              maxRetries: 2,
              nextStageId: null
            }
          },
          {
            id: 4,
            question: "Are you the owner or tenant?",
            answers: [
              {
                id: 7,
                text: "OWNER",
                response: "Great! We'll send you owner-specific energy improvement information. Thanks for participating!",
                nextStageId: null
              },
              {
                id: 8,
                text: "TENANT",
                response: "Perfect! We'll send you tenant-friendly energy-saving tips. Thanks for your time!",
                nextStageId: null
              }
            ],
            isInitial: false,
            defaultResponse: {
              message: "Please let me know if you are the 'Owner' or 'Tenant' of your apartment.",
              retryMessage: "I need to know if you're the 'Owner' or 'Tenant' to provide the right information.",
              maxRetries: 2,
              nextStageId: null
            }
          }
        ],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      smsTemplateService.saveTemplate(sampleTemplate);
    }
  };

  // Initialize a new stage
  const createNewStage = () => ({
    id: Date.now(),
    question: '',
    answers: [],
    isInitial: false,
    defaultResponse: {
      message: "I didn't understand your response. Please choose from the options provided.",
      retryMessage: "Please select one of the available options to continue.",
      maxRetries: 3,
      nextStageId: null
    }
  });

  // Add a new stage to the current template
  const addStage = () => {
    const newStage = createNewStage();
    if (currentTemplate.stages.length === 0) {
      newStage.isInitial = true;
    }
    
    setCurrentTemplate(prev => {
      // Auto-connect previous stage's answers to this new stage if they don't have a nextStageId
      const updatedStages = prev.stages.map((stage, index) => {
        // Only auto-connect the last stage's answers
        if (index === prev.stages.length - 1) {
          return {
            ...stage,
            answers: stage.answers.map(answer => {
              // If answer doesn't have a nextStageId set, connect it to the new stage
              if (answer.nextStageId === null || answer.nextStageId === undefined) {
                return {
                  ...answer,
                  nextStageId: newStage.id
                };
              }
              return answer;
            })
          };
        }
        return stage;
      });
      
      return {
        ...prev,
        stages: [...updatedStages, newStage]
      };
    });
  };

  // Remove a stage
  const removeStage = (stageId) => {
    setCurrentTemplate(prev => {
      const updatedStages = prev.stages.filter(stage => stage.id !== stageId);
      // If we removed the initial stage, make the first remaining stage initial
      if (updatedStages.length > 0 && !updatedStages.some(s => s.isInitial)) {
        updatedStages[0].isInitial = true;
      }
      return {
        ...prev,
        stages: updatedStages
      };
    });
  };

  // Update stage question
  const updateStageQuestion = (stageId, question) => {
    setCurrentTemplate(prev => ({
      ...prev,
      stages: prev.stages.map(stage => 
        stage.id === stageId ? { ...stage, question } : stage
      )
    }));
  };

  // Add answer option to a stage
  const addAnswer = (stageId) => {
    const newAnswer = {
      id: Date.now(),
      text: '',
      nextStageId: null,
      response: ''  // Empty by default - will be filled based on flow
    };
    
    setCurrentTemplate(prev => ({
      ...prev,
      stages: prev.stages.map(stage => 
        stage.id === stageId 
          ? { ...stage, answers: [...stage.answers, newAnswer] }
          : stage
      )
    }));
  };

  // Remove answer option
  const removeAnswer = (stageId, answerId) => {
    setCurrentTemplate(prev => ({
      ...prev,
      stages: prev.stages.map(stage => 
        stage.id === stageId 
          ? { ...stage, answers: stage.answers.filter(answer => answer.id !== answerId) }
          : stage
      )
    }));
  };

  // Update answer details
  const updateAnswer = (stageId, answerId, field, value) => {
    setCurrentTemplate(prev => ({
      ...prev,
      stages: prev.stages.map(stage => 
        stage.id === stageId 
          ? {
              ...stage,
              answers: stage.answers.map(answer => 
                answer.id === answerId ? { ...answer, [field]: value } : answer
              )
            }
          : stage
      )
    }));
  };

  // Update default response details
  // Assessors come from the shared Booking Options record, so this list is the
  // same one the Booking tab offers and stays in step with it.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await bookingOptionsService.loadOptions(instance);
        if (!cancelled) {
          setAssessorOptions(result?.options?.assessorOptions || result?.assessorOptions || []);
        }
      } catch (error) {
        console.error('Could not load the shared assessor list:', error);
      }
    })();
    return () => { cancelled = true; };
  }, [instance]);

  const templateTokens = useMemo(
    () => collectTemplateTokens(currentTemplate),
    [currentTemplate]
  );

  const setPlaceholderValue = useCallback((token, value) => {
    setCurrentTemplate((prev) => ({
      ...prev,
      placeholderValues: { ...(prev.placeholderValues || {}), [token]: value }
    }));
  }, []);

  // Remember the last text box the user was in, so Insert knows where to put
  // the placeholder. One document-level listener rather than a handler on
  // every field in the builder.
  useEffect(() => {
    const handleFocusIn = (event) => {
      const el = event.target;
      if (!el) return;
      const isTextArea = el.tagName === 'TEXTAREA';
      const isTextInput = el.tagName === 'INPUT' && (el.type === 'text' || el.type === '');
      if (isTextArea || isTextInput) {
        lastFocusedFieldRef.current = el;
      }
    };
    document.addEventListener('focusin', handleFocusIn);
    return () => document.removeEventListener('focusin', handleFocusIn);
  }, []);

  /**
   * Insert {{token}} at the cursor of whichever box was last used. React keeps
   * its own copy of an input's value, so the native setter has to be used and
   * an input event dispatched, otherwise React overwrites the change.
   */
  const insertPlaceholder = useCallback((token) => {
    const text = `{{${token}}}`;
    const el = lastFocusedFieldRef.current;

    if (!el || !document.body.contains(el)) {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(text);
        showAlert(`${text} copied. Click into a box first to insert it directly.`, 'info');
      } else {
        showAlert(`Click into a message box first, then insert ${text}.`, 'warning');
      }
      return;
    }

    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    const nextValue = `${el.value.slice(0, start)}${text}${el.value.slice(end)}`;

    const prototype = el.tagName === 'TEXTAREA'
      ? window.HTMLTextAreaElement.prototype
      : window.HTMLInputElement.prototype;
    const nativeSetter = Object.getOwnPropertyDescriptor(prototype, 'value')?.set;
    if (nativeSetter) {
      nativeSetter.call(el, nextValue);
    } else {
      el.value = nextValue;
    }
    el.dispatchEvent(new Event('input', { bubbles: true }));

    el.focus();
    const caret = start + text.length;
    if (el.setSelectionRange) {
      el.setSelectionRange(caret, caret);
    }
  }, []);

  const updateStageDefaultResponse = (stageId, field, value) => {
    setCurrentTemplate(prev => ({
      ...prev,
      stages: prev.stages.map(stage => 
        stage.id === stageId 
          ? {
              ...stage,
              defaultResponse: {
                ...stage.defaultResponse,
                [field]: value
              }
            }
          : stage
      )
    }));
  };

  // Save template
  const saveTemplate = async () => {
    // Validate template
    const errors = smsTemplateService.validateTemplate(currentTemplate);
    if (errors.length > 0) {
      showAlert(errors.join('. '), 'danger');
      return;
    }

    // The scheme decides which phone number customers are given, so it has to
    // be chosen deliberately rather than defaulted to whichever came first.
    if (!currentTemplate.scheme) {
      showAlert('Choose which scheme this template is sent under before saving.', 'danger');
      return;
    }

    try {
      if (useBackendStorage) {
        // Clean payload for backend - just send the template as-is, backend will handle field mapping
        // Build stage ID mapping to indices
        const stageIdMap = {};
        currentTemplate.stages.forEach((stage, index) => {
          stageIdMap[stage.id] = index + 1; // Backend uses 1-based StageNumber
        });
        
        const cleanTemplate = {
          name: currentTemplate.name,
          description: currentTemplate.description,
          stages: currentTemplate.stages.map((stage, stageIndex) => ({
            question: stage.question,
            isInitial: stage.isInitial,
            answers: stage.answers.map(answer => {
              // Convert stage ID to stage number (1-based index) for backend
              const nextStageNumber = answer.nextStageId ? stageIdMap[answer.nextStageId] : null;
              
              return {
                text: answer.text,
                response: answer.response || '',
                nextStageNumber: nextStageNumber
              };
            }),
            defaultResponse: null
          }))
        };

        // One invalid-response message for the whole template. The old
        // per-stage boxes meant typing the same sentence into every stage, and
        // the API never read them back, so they were always blank on reload.
        cleanTemplate.invalidResponseMessage = String(currentTemplate.invalidResponseMessage || '').trim();
        cleanTemplate.scheme = currentTemplate.scheme || '';
        cleanTemplate.placeholderValues = currentTemplate.placeholderValues || {};
        
        let response;
        if (currentTemplate.id) {
          response = await smsApiService.updateTemplate(currentTemplate.id, cleanTemplate, instance);
        } else {
          response = await smsApiService.saveTemplate(cleanTemplate, instance);
          // After creating a new template, update the current template with the backend's assigned ID
          if (response.data && response.data.Id) {
            setCurrentTemplate(prev => ({
              ...prev,
              id: response.data.Id
            }));
          }
        }
      } else {
        smsTemplateService.saveTemplate(currentTemplate);
      }
      
      showAlert(`Template "${currentTemplate.name}" saved successfully!`, 'success');
      loadTemplates(); // Reload templates
      // Don't reset the template - keep it open for further editing
    } catch (error) {
      console.error('Error saving template:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Error saving template. Please try again.';
      
      // Check if it's a foreign key constraint error during update
      if (currentTemplate.id && (errorMessage.includes('REFERENCE constraint') || errorMessage.includes('FK_SMS_Conversations'))) {
        showAlert('Cannot update template: It has active SMS conversations. Please create a new version of this template instead (the old one will remain for active conversations).', 'warning');
      } else {
        showAlert(errorMessage, 'danger');
      }
    }
  };

  // Reset current template
  const resetCurrentTemplate = () => {
    setCurrentTemplate({
      id: null,
      name: '',
      description: '',
      scheme: '',
      invalidResponseMessage: '',
      placeholderValues: {},
      stages: []
    });
  };

  // Load template for editing
  // Ensure template has proper IDs for React keys
  const ensureTemplateHasIds = (template) => {
    // Handle both backend (PascalCase) and local storage (camelCase) field names
    const normalizedTemplate = {
      id: template.id || template.Id,
      name: template.name || template.Name || '',
      description: template.description || template.Description || '',
      scheme: template.scheme || template.Scheme || '',
      placeholderValues: template.placeholderValues || template.PlaceholderValues || {},
      invalidResponseMessage: template.invalidResponseMessage
        || template.InvalidResponseMessage
        || '',
      stages: template.stages || template.Stages || [],
      createdAt: template.createdAt || template.CreatedAt,
      updatedAt: template.updatedAt || template.UpdatedAt
    };
    
    const templateWithIds = {
      ...normalizedTemplate,
      stages: normalizedTemplate.stages?.map((stage, stageIndex) => {
        // Handle both camelCase and PascalCase for answers array
        const answers = stage.answers || stage.Answers || [];
        
        // Check if this stage should be initial
        // Use existing isInitial if set, otherwise make first stage initial
        const shouldBeInitial = stage.isInitial ?? stage.IsInitial ?? (stageIndex === 0);
        
        const normalizedStage = {
          id: stage.id || stage.Id || `stage-${Date.now()}-${stageIndex}`,
          question: stage.question || stage.Question || '',
          isInitial: shouldBeInitial,
          answers: answers.map((answer, answerIndex) => ({
            id: answer.id || answer.Id || `answer-${Date.now()}-${stageIndex}-${answerIndex}`,
            text: answer.text || answer.Text || answer.answerText || answer.AnswerText || '',
            response: answer.response || answer.Response || answer.responseMessage || answer.ResponseMessage || '',
            nextStageId: answer.nextStageId ?? answer.NextStageId ?? null
          })),
          defaultResponse: stage.defaultResponse || stage.DefaultResponse || {
            message: '',
            retryMessage: '',
            maxRetries: 3,
            nextStageId: null
          }
        };
        
        return normalizedStage;
      }) || []
    };
    
    return templateWithIds;
  };

  const loadTemplate = async (template) => {
    try {
      // Fetch full template details from backend to get answers
      if (useBackendStorage && template.Id) {
        const response = await smsApiService.getTemplate(template.Id, instance);
        const fullTemplate = response.data;
        const templateWithIds = ensureTemplateHasIds(fullTemplate);
        setCurrentTemplate(templateWithIds);
      } else {
        const templateWithIds = ensureTemplateHasIds(template);
        setCurrentTemplate(templateWithIds);
      }
      setShowModal(false);
    } catch (error) {
      console.error('Error loading template details:', error);
      // Fallback to template from list
      const templateWithIds = ensureTemplateHasIds(template);
      setCurrentTemplate(templateWithIds);
      setShowModal(false);
    }
  };

  // Delete template
  const deleteTemplate = async (templateId) => {
    try {
      if (useBackendStorage) {
        await smsApiService.deleteTemplate(templateId, instance);
      } else {
        smsTemplateService.deleteTemplate(templateId);
      }
      
      // If the deleted template is currently being edited, reset the form
      if (currentTemplate.id === templateId) {
        resetCurrentTemplate();
      }
      
      loadTemplates(); // Reload templates
      showAlert('Template deleted successfully!', 'success');
    } catch (error) {
      console.error('Error deleting template:', error);
      
      // Check if it's a foreign key constraint error
      if (error.response?.data?.error?.includes('REFERENCE constraint') || 
          error.response?.data?.error?.includes('FK_SMS_Conversations') ||
          error.response?.status === 409) {
        showAlert('Cannot delete template: It is currently being used by active SMS conversations. Please end or reassign those conversations first.', 'warning');
      } else {
        showAlert('Error deleting template. Please try again.', 'danger');
      }
    }
  };

  // Export template
  const exportTemplate = (templateId) => {
    const success = smsTemplateService.exportTemplate(templateId);
    if (success) {
      showAlert('Template exported successfully!', 'success');
    } else {
      showAlert('Error exporting template.', 'danger');
    }
  };

  // Import template
  const importTemplate = () => {
    if (!importJson.trim()) {
      showAlert('Please paste template JSON data', 'danger');
      return;
    }

    const template = smsTemplateService.importTemplate(importJson);
    if (template) {
      loadTemplates(); // Reload templates
      showAlert(`Template "${template.name}" imported successfully!`, 'success');
      setShowImportModal(false);
      setImportJson('');
    } else {
      showAlert('Error importing template. Please check the JSON format.', 'danger');
    }
  };

  // Duplicate template
  const duplicateTemplate = (template) => {
    const duplicatedTemplate = {
      ...template,
      id: Date.now(),
      name: `${template.name} (Copy)`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    const success = smsTemplateService.saveTemplate(duplicatedTemplate);
    if (success) {
      loadTemplates();
      showAlert(`Template "${duplicatedTemplate.name}" duplicated successfully!`, 'success');
    } else {
      showAlert('Error duplicating template.', 'danger');
    }
  };

  // Show alert helper
  const showAlert = (message, type) => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: '', type: 'success' }), 3000);
  };

  // Get stage by ID
  const getStageById = (stageId) => {
    return currentTemplate.stages.find(stage => stage.id == stageId); // Use == for loose comparison
  };

  // Preview component
  const PreviewModal = () => (
    <Modal show={showPreview} onHide={() => setShowPreview(false)} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>SMS Template Preview: {currentTemplate.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="conversation-preview">
          {currentTemplate.stages.map((stage, stageIndex) => (
            <Card key={stage.id || `stage-${stageIndex}`} className="mb-3">
              <Card.Header className="d-flex align-items-center">
                <FontAwesomeIcon icon={faComments} className="me-2" />
                <strong>
                  {stage.isInitial ? 'Initial Question' : `Stage ${currentTemplate.stages.indexOf(stage) + 1}`}
                </strong>
              </Card.Header>
              <Card.Body>
                <div className="sms-bubble bot-message mb-3">
                  <strong>Bot:</strong> {stage.question}
                </div>
                
                {stage.answers.map((answer, answerIndex) => (
                  <div key={answer.id || `answer-${stageIndex}-${answerIndex}`} className="answer-option mb-2">
                    <div className="d-flex align-items-center">
                      <div className="sms-bubble user-message me-2" style={{ minWidth: '80px' }}>
                        <strong>"{answer.text}"</strong>
                      </div>
                      <FontAwesomeIcon icon={faArrowRight} className="me-2 text-muted" />
                      <div className="text-muted small">
                        {answer.nextStageId ? (
                          <>
                            <strong>Goes to:</strong> {getStageById(answer.nextStageId)?.question || 'Unknown Stage'}
                          </>
                        ) : (
                          <strong>Ends conversation</strong>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Default Response Preview */}
                {stage.defaultResponse && (
                  <div className="mt-3 p-2 bg-light rounded">
                    <small className="text-muted">
                      <strong>Default Response:</strong> {stage.defaultResponse.message}
                    </small>
                    {stage.defaultResponse.retryMessage && (
                      <div>
                        <small className="text-muted">
                          <strong>Retry Message:</strong> {stage.defaultResponse.retryMessage}
                        </small>
                      </div>
                    )}
                    <div>
                      <small className="text-muted">
                        Max Retries: {stage.defaultResponse.maxRetries || 3}
                      </small>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          ))}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowPreview(false)}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );

  // Template list modal
  const TemplateListModal = () => (
    <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Saved Templates ({filteredTemplates.length}/{templates.length})</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="mb-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label className="small text-muted">Template Category</Form.Label>
              <Form.Select
                size="sm"
                value={templateCategoryFilter}
                onChange={(event) => setTemplateCategoryFilter(event.target.value)}
              >
                <option value="all">All Templates</option>
                <option value="general">General Templates</option>
                <option value="booking">Booking Templates</option>
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        {filteredTemplates.length === 0 ? (
          <Alert variant="info">
            {templates.length === 0
              ? 'No templates saved yet. Create your first SMS conversation template!'
              : 'No templates match the selected category filter.'}
          </Alert>
        ) : (
          <ListGroup>
            {filteredTemplates.map(template => (
              <ListGroup.Item key={template.Id || template.id} className="template-item">
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">
                    <h6 className="mb-1">{template.name || template.Name || 'Untitled Template'}</h6>
                    <p className="mb-1 text-muted">{template.description || template.Description || 'No description'}</p>
                    <div className="d-flex align-items-center">
                      <Badge bg="primary" className="me-2">{(template.stages || template.Stages || []).length} stages</Badge>
                      <small className="text-muted">
                        {template.createdAt && !isNaN(new Date(template.createdAt).getTime()) ? (
                          <>
                            Created: {new Date(template.createdAt).toLocaleDateString('en-GB')}
                            {template.updatedAt && template.updatedAt !== template.createdAt && !isNaN(new Date(template.updatedAt).getTime()) && (
                              <span> • Updated: {new Date(template.updatedAt).toLocaleDateString('en-GB')}</span>
                            )}
                          </>
                        ) : (
                          <span>Template ID: {template.id || template.Id}</span>
                        )}
                      </small>
                    </div>
                  </div>
                  <div className="d-flex flex-column">
                    <div className="mb-2">
                      <Button 
                        variant="outline-primary" 
                        size="sm" 
                        className="me-1"
                        onClick={() => loadTemplate(template)}
                      >
                        <FontAwesomeIcon icon={faEdit} /> Edit
                      </Button>
                      <Dropdown as="span" className="d-inline-block">
                        <Dropdown.Toggle variant="outline-secondary" size="sm" id={`dropdown-${template.Id || template.id}`}>
                          <FontAwesomeIcon icon={faEye} />
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item onClick={() => duplicateTemplate(template)}>
                            <FontAwesomeIcon icon={faCopy} className="me-2" />
                            Duplicate
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => exportTemplate(template.Id || template.id)}>
                            <FontAwesomeIcon icon={faDownload} className="me-2" />
                            Export JSON
                          </Dropdown.Item>
                          <Dropdown.Divider />
                          <Dropdown.Item 
                            onClick={() => deleteTemplate(template.Id || template.id)}
                            className="text-danger"
                          >
                            <FontAwesomeIcon icon={faTrash} className="me-2" />
                            Delete
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>
                  </div>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowModal(false)}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );

  // Import modal
  const ImportModal = () => (
    <Modal show={showImportModal} onHide={() => setShowImportModal(false)} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Import SMS Template</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="info">
          Paste the JSON data of an exported SMS template below to import it.
        </Alert>
        <Form.Group className="mb-3">
          <Form.Label>Template JSON Data</Form.Label>
          <Form.Control
            as="textarea"
            rows={10}
            placeholder="Paste template JSON here..."
            value={importJson}
            onChange={(e) => setImportJson(e.target.value)}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowImportModal(false)}>
          Cancel
        </Button>
        <Button variant="primary" onClick={importTemplate}>
          <FontAwesomeIcon icon={faUpload} className="me-2" />
          Import Template
        </Button>
      </Modal.Footer>
    </Modal>
  );

  return (
    <Container fluid className="mt-4">
      <Row>
        <Col>
          <Card>
            <Card.Header className="sms-template-header">
              <div className="d-flex justify-content-between align-items-center">
                <h4 className="mb-0">
                  <FontAwesomeIcon icon={faComments} className="me-2" />
                  SMS Conversation Template Builder
                </h4>
                <div className="d-flex gap-2">
                  <Button 
                    variant="light" 
                    onClick={() => setShowModal(true)}
                    title="Load existing template"
                  >
                    <FontAwesomeIcon icon={faEdit} /> Load Template
                  </Button>
                  <Button 
                    variant="light" 
                    onClick={() => setShowImportModal(true)}
                    title="Import template from JSON"
                  >
                    <FontAwesomeIcon icon={faFileImport} /> Import
                  </Button>
                  <Button 
                    variant="light" 
                    onClick={() => setShowPreview(true)}
                    disabled={currentTemplate.stages.length === 0}
                    title="Preview conversation flow"
                  >
                    <FontAwesomeIcon icon={faEye} /> Preview
                  </Button>
                  <Button 
                    variant="warning"
                    onClick={saveTemplate}
                    title="Save current template"
                  >
                    <FontAwesomeIcon icon={faSave} /> Save Template
                  </Button>
                  {(currentTemplate.name || currentTemplate.stages.length > 0) && (
                    <Button 
                      variant="outline-light"
                      onClick={resetCurrentTemplate}
                      title="Start new template"
                    >
                      <FontAwesomeIcon icon={faPlus} /> New
                    </Button>
                  )}
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              {alert.show && (
                <Alert variant={alert.type} dismissible onClose={() => setAlert({ ...alert, show: false })}>
                  {alert.message}
                </Alert>
              )}

              <Alert variant="light" className="border">
                <h6 className="mb-2">Using Placeholders In Templates</h6>
                <div className="small">
                  Click into any message box, then click a placeholder below to insert it at the cursor.
                  Placeholders go in double curly braces, for example {'{{firstName}}'}.
                </div>
                <div className="small mt-1">
                  They work in stage questions and in response messages.
                </div>

                {KNOWN_PLACEHOLDERS.map((section) => (
                  <div key={section.group} className="mt-2">
                    <div className="small fw-bold">{section.group}</div>
                    {section.hint && <div className="small text-muted">{section.hint}</div>}
                    <div className="d-flex flex-wrap gap-1 mt-1">
                      {section.items.map((item) => (
                        <Button
                          key={item.token}
                          variant="outline-secondary"
                          size="sm"
                          className="py-0 px-2"
                          style={{ fontSize: '0.75rem' }}
                          title={item.note}
                          onClick={() => insertPlaceholder(item.token)}
                        >
                          {`{{${item.token}}}`}
                        </Button>
                      ))}
                    </div>
                  </div>
                ))}

                <div className="small mt-2 text-muted">
                  You are not limited to these - any {'{{name}}'} works. Whatever you use appears in the
                  "Placeholder values" box below, where you either type a value or leave it to be supplied
                  per recipient (a column called <code>orderRef</code> in the recipient list fills
                  {' '}{'{{orderRef}}'}). A placeholder with nothing to fill it blocks the send rather than
                  going out half-finished.
                </div>
              </Alert>

              <Alert variant="info" className="d-flex justify-content-between align-items-center">
                <div>
                  <strong>New:</strong> Pre-send message preview and send-time text edits are now available when starting a conversation.
                  <div className="small text-muted">Go to SMS Conversation Manager, click Start New, then choose your template.</div>
                </div>
                <Button variant="outline-primary" size="sm" onClick={() => navigate('/sms-conversations')}>
                  Open Conversation Manager
                </Button>
              </Alert>

              {/* Template Basic Info */}
              <Row className="mb-4">
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Scheme *</Form.Label>
                    <Form.Select
                      value={currentTemplate.scheme || ''}
                      onChange={(e) => setCurrentTemplate(prev => ({ ...prev, scheme: e.target.value }))}
                      isInvalid={!currentTemplate.scheme}
                    >
                      <option value="">Choose a scheme...</option>
                      {SCHEME_CHOICES.map((scheme) => (
                        <option key={scheme.id} value={scheme.id}>{scheme.label}</option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      {currentTemplate.scheme
                        ? `Customers on this template will be given ${schemePhone(currentTemplate.scheme)}.`
                        : 'Decides which phone number customers are given. Required.'}
                    </Form.Text>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Template Name *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter template name"
                      value={currentTemplate.name}
                      onChange={(e) => setCurrentTemplate(prev => ({ ...prev, name: e.target.value }))}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Brief description of this template"
                      value={currentTemplate.description}
                      onChange={(e) => setCurrentTemplate(prev => ({ ...prev, description: e.target.value }))}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Values for the placeholders this template actually uses. The
                  list is built by scanning the template, so it shrinks and
                  grows as placeholders are added or removed. */}
              {templateTokens.length > 0 && (
                <Card className="mb-3 border">
                  <Card.Header className="py-2 bg-light">
                    <strong>Placeholder values for this template</strong>
                    <div className="small text-muted">
                      {templateTokens.length} placeholder{templateTokens.length === 1 ? '' : 's'} used.
                      Anything left blank must be supplied by the recipient list, or the send will be blocked.
                    </div>
                  </Card.Header>
                  <Card.Body className="py-2">
                    <Row>
                      {templateTokens.map((token) => {
                        const schemeField = SCHEME_DERIVED_TOKENS[token];
                        const scheme = schemeDetail(currentTemplate.scheme);
                        const isAssessor = ASSESSOR_TOKENS.includes(token);
                        const isPerRecipient = PER_RECIPIENT_TOKENS.includes(token);
                        const value = currentTemplate.placeholderValues?.[token] || '';

                        return (
                          <Col md={6} key={token} className="mb-2">
                            <Form.Label className="small mb-1">
                              <code>{`{{${token}}}`}</code>
                            </Form.Label>

                            {schemeField ? (
                              <>
                                <Form.Control
                                  size="sm"
                                  readOnly
                                  value={scheme ? scheme[schemeField] || '' : ''}
                                  placeholder="Choose a scheme above"
                                />
                                <Form.Text className="text-muted">
                                  Comes from the scheme. Change the scheme to change this.
                                </Form.Text>
                              </>
                            ) : isAssessor ? (
                              <>
                                <Form.Select
                                  size="sm"
                                  value={value}
                                  onChange={(e) => setPlaceholderValue(token, e.target.value)}
                                >
                                  <option value="">Choose an assessor...</option>
                                  {assessorOptions.map((name) => (
                                    <option key={name} value={name}>{name}</option>
                                  ))}
                                  {value && !assessorOptions.includes(value) && (
                                    <option value={value}>{value}</option>
                                  )}
                                </Form.Select>
                                <Form.Text className="text-muted">
                                  From the shared assessor list used by the Booking tab.
                                </Form.Text>
                              </>
                            ) : (
                              <>
                                <Form.Control
                                  size="sm"
                                  value={value}
                                  onChange={(e) => setPlaceholderValue(token, e.target.value)}
                                  placeholder={isPerRecipient ? 'Usually left blank' : 'Enter a value'}
                                />
                                {isPerRecipient && (
                                  <Form.Text className="text-muted">
                                    Normally supplied per recipient. Only fill this in if every
                                    recipient should get the same value.
                                  </Form.Text>
                                )}
                              </>
                            )}
                          </Col>
                        );
                      })}
                    </Row>
                  </Card.Body>
                </Card>
              )}

              {/* Stages */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5>Conversation Stages</h5>
                <Button variant="primary" onClick={addStage}>
                  <FontAwesomeIcon icon={faPlus} /> Add Stage
                </Button>
              </div>

              {currentTemplate.stages.map((stage, index) => (
                <Card key={stage.id || `form-stage-${index}`} className="mb-3">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>
                        {stage.isInitial ? 'Initial Question' : `Stage ${index + 1}`}
                      </strong>
                      {stage.isInitial && <Badge bg="success" className="ms-2">Starting Point</Badge>}
                    </div>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => removeStage(stage.id)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>
                  </Card.Header>
                  <Card.Body>
                    {/* Stage Question */}
                    <Form.Group className="mb-3">
                      <Form.Label>Question/Message</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        placeholder="Enter the question or message for this stage"
                        value={stage.question}
                        onChange={(e) => updateStageQuestion(stage.id, e.target.value)}
                      />
                      <Form.Text className="text-muted">
                        You can include placeholders like {'{{firstName}}'} and {'{{postcode}}'}.
                      </Form.Text>
                    </Form.Group>

                    {/* Answer Options */}
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6>Answer Options</h6>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => addAnswer(stage.id)}
                      >
                        <FontAwesomeIcon icon={faPlus} /> Add Answer Option
                      </Button>
                    </div>

                    {stage.answers.map((answer, answerIndex) => (
                      <Card key={answer.id || `form-answer-${index}-${answerIndex}`} className="mb-2" style={{ backgroundColor: '#f8f9fa' }}>
                        <Card.Body className="py-2">
                          <Row>
                            <Col md={3}>
                              <Form.Group className="mb-2">
                                <Form.Label className="small">
                                  <strong>Answer Option</strong>
                                </Form.Label>
                                <Form.Control
                                  type="text"
                                  placeholder="e.g., YES, NO"
                                  value={answer.text}
                                  onChange={(e) => updateAnswer(stage.id, answer.id, 'text', e.target.value)}
                                />
                                <Form.Text className="text-muted">
                                  Customer types this
                                </Form.Text>
                              </Form.Group>
                            </Col>
                            <Col md={5}>
                              <Form.Group className="mb-2">
                                <Form.Label className="small">
                                  <strong>Response Message</strong>
                                </Form.Label>
                                <Form.Control
                                  type="text"
                                  placeholder={answer.nextStageId ? "Leave empty to go directly to next question" : "Thank you message (required for ending)"}
                                  value={answer.response || ''}
                                  onChange={(e) => updateAnswer(stage.id, answer.id, 'response', e.target.value)}
                                />
                                <Form.Text className="text-muted">
                                  {answer.nextStageId ? 
                                    "💡 Empty = no extra message, goes straight to next question" : 
                                    "⚠️ Required when ending conversation"
                                  }
                                </Form.Text>
                                <Form.Text className="text-muted d-block">
                                  Placeholders are supported here too, for example {'{{preferredDayTime}}'}.
                                </Form.Text>
                              </Form.Group>
                            </Col>
                            <Col md={3}>
                              <Form.Group className="mb-2">
                                <Form.Label className="small">
                                  <strong>Then...</strong>
                                </Form.Label>
                                <Form.Select
                                  value={answer.nextStageId || ''}
                                  onChange={(e) => {
                                    const nextStageId = e.target.value ? parseInt(e.target.value) : null;
                                    updateAnswer(stage.id, answer.id, 'nextStageId', nextStageId);
                                    // Auto-clear response if going to next stage
                                    if (nextStageId && answer.response) {
                                      updateAnswer(stage.id, answer.id, 'response', '');
                                    }
                                  }}
                                >
                                  <option value="">End here</option>
                                  {currentTemplate.stages
                                    .filter(s => s.id !== stage.id)
                                    .map((s, optionIndex) => (
                                      <option key={s.id || `next-stage-${optionIndex}`} value={s.id}>
                                        Stage: {s.question ? s.question.substring(0, 20) : 'Unnamed Stage'}...
                                      </option>
                                    ))}
                                </Form.Select>
                              </Form.Group>
                            </Col>
                            <Col md={1} className="d-flex align-items-center">
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => removeAnswer(stage.id, answer.id)}
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </Button>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    ))}

                    {stage.answers.length === 0 && (
                      <Alert variant="info" className="small mb-0">
                        Add answer options for users to respond to this message.
                      </Alert>
                    )}

                  </Card.Body>
                </Card>
              ))}

              {currentTemplate.stages.length === 0 && (
                <Alert variant="info">
                  <h5>📱 Welcome to SMS Template Builder!</h5>
                  <p className="mb-0">Click "Add Stage" below to create your first conversation step.</p>
                </Alert>
              )}

              {/* One invalid-response message for the whole template. This used
                  to be a box on every stage, which meant typing the same
                  sentence over and over - and the value was never read back, so
                  it always looked blank. */}
              <div className="mt-4 border-top pt-3">
                <h6 className="mb-1">
                  <FontAwesomeIcon icon={faReply} className="me-2" />
                  When a customer replies with something unexpected
                </h6>
                <Form.Text className="text-muted d-block mb-2">
                  Sent when a reply matches none of the options on the stage the customer is at.
                  Applies to the whole template.
                </Form.Text>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Leave blank and the system will write one for you"
                  value={currentTemplate.invalidResponseMessage || ''}
                  onChange={(e) => setCurrentTemplate({
                    ...currentTemplate,
                    invalidResponseMessage: e.target.value
                  })}
                />
                <Form.Text className="text-muted d-block mt-1">
                  {String(currentTemplate.invalidResponseMessage || '').trim()
                    ? 'This message will be sent, with any placeholders filled in.'
                    : 'Left blank, the system builds the message from the options that stage actually accepts, '
                      + `and uses this scheme's phone number${schemePhone(currentTemplate.scheme) ? ` (${schemePhone(currentTemplate.scheme)})` : ''}. `
                      + 'For example: "Sorry, we did not understand your reply. Please reply CALL, TEXT or WRONG. '
                      + `Or call ${schemePhone(currentTemplate.scheme) || '...'} (9am-5pm Mon-Fri)..." `
                      + 'That is usually the better choice, because it stays correct when the options change.'}
                </Form.Text>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <PreviewModal />
      <TemplateListModal />
      <ImportModal />

      <style>{`
        .sms-bubble {
          padding: 10px 15px;
          border-radius: 15px;
          margin: 5px 0;
          max-width: 80%;
          white-space: pre-line;
        }
        
        .bot-message {
          background-color: #e3f2fd;
          border: 1px solid #2196f3;
        }
        
        .user-message {
          background-color: #f3e5f5;
          border: 1px solid #9c27b0;
          margin-left: auto;
        }
        
        .answer-option {
          border-left: 3px solid #9c27b0;
          padding-left: 15px;
          margin-left: 20px;
        }
        
        .conversation-preview {
          max-height: 500px;
          overflow-y: auto;
        }
      `}</style>
    </Container>
  );
};

export default SMSTemplateBuilder;